import { useState, useEffect, useRef, useCallback } from "react";
import { useFilterContext } from "./ContextManager";

// Components
import { ContextMenu } from "./ContextMenu";
import { SearchBar } from "./UX/SearchBar/SearchBar.jsx";
import { MapMarkerTooltip } from "./UX/MapMarkerTooltip/MapMarkerTooltip";
import { PolylineTooltip } from "./UX/PolylineTooltip/PolylineTooltip";
import { CustomAirportForm } from "./CustomAirportForm.jsx";
import { LoadDataConfirmationModal } from "./LoadDataConfirmationModal.jsx";
import { CustomUserButton } from "./UX/ClerkUserButton/ClerkUserButton.jsx";
import { BottomMapControls } from "./BottomMapControls.jsx";

// MUI
import { Fab } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";

// CSS
import style from "../css/mapComponent.module.css";

// Map Styles
import { mapStyles } from "../theme/mapStyles";

//Utility functions
import {
  delay,
  debounce,
  calculateMultiplier,
  timeConverter,
  centerMapOnPolyline,
  centerMapOnPoint,
} from "../utils/utilityFunctions";

import { decompressObjectFromURL } from "../data/schemas/filterCodes";

//SVG
import { createAdvancedMarkerSvg } from "../assets/svg/advancedMarkerSvg.jsx";

//geojson
import { handleGeoJSON } from "../utils/variables";

//Google Maps Api
import { Loader } from "@googlemaps/js-api-loader";

//ENV
const MAPS_KEY = import.meta.env.VITE_MAPS_KEY;

export function MapComponent({ map, updateMap }) {
  //context provider variables
  const {
    filters,
    updateFilters,
    filteredAirportsResult,
    filteredRoutesResult,
    selectedAirports,
    toggleSelectAirport,
    clearSelectedAirports,
    updateModalState,
    airportIdMap,
    updateLastMapCenter,
    updateLastMapZoom,
    updateApiLoader,
    selectedRoutes,
    toggleSelectRoute,
    clearSelectedRoutes,
    updateRouteModalState,
    customAirportForm,
    updateCustomAirportForm,
    updateEditingAirport,
    restoreDefaultCount,
    searchBarIsOpen,
    updateSearchBarIsOpen,
    updateSummaryOpen,
    geoJsonData,
    updateGeoJsonData,
    mapFocusRequest,
  } = useFilterContext();

  const mapRef = useRef(null);

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    lat: 0,
    lng: 0,
  });
  const [rangeCircles, setRangeCircles] = useState([]);
  // array of { circle, centerMarker, distanceLabel }
  const [radius, setRadius] = useState(200); // radius of the range circle (km)

  const [activeMarker, setActiveMarker] = useState(null); //which airport marker is being interacted with
  const [activeMarkerData, setActiveMarkerData] = useState(null); //updated activeMarker data, for use in marker tooltips
  const [markersState, setMarkersState] = useState(null); // tracks when markers are rendered
  const [isMarkerActive, setIsMarkerActive] = useState(false); //if a marker is hovered over or clicked
  const [activePolyline, setActivePolyline] = useState(null); //which route/polyline is being interacted with
  const [currentPolyline, setCurrentPolyline] = useState(null); //used to change styling of polyline while hovering over both a polyline and a marker
  const [isPolylineActive, setIsPolylineActive] = useState(false); //if a polyline is hovered over or clicked
  const [polylinesState, setPolylinesState] = useState(null); // tracks when polylines are rendered
  const [mousePos, setMousePos] = useState({}); //where polyline tooltip is rendered when hovering over a polyline
  const [isTooltipVisible, setIsTooltipVisible] = useState(false); //tracks visibilty of tooltip, set to false when dragging/panning the map to close all tooltips while dragging
  const [isMapDragging, setIsMapDragging] = useState(false);
  const [customAirportCoords, setCustomAirportCoords] = useState(null);
  // const [geoJsonData, setGeoJsonData] = useState(null);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [geoJsonMousePos, setGeoJsonMousePos] = useState(null);
  const [
    selectionViewHighlightedAirports,
    setSelectionViewHighlightedAirports,
  ] = useState([]);
  const [selectionViewHighlightedRoutes, setSelectionViewHighlightedRoutes] =
    useState([]);

  const [markerColors, setMarkerColors] = useState({
    small: "#fff",
    medium: "#fff",
    large: "#fff",
    seaplane: "#fff",
    custom: "#fff",
    domestic: "#fff",
    international: "#fff",
    customRoute: "#fff",
    commercialRoute: "#fff",
    psoRoute: "#fff",
  });

  const googleMapRef = useRef(null);
  // Use a Map object to keep track of markers by airportId
  const markersRef = useRef(new Map());
  const polylinesRef = useRef([]);
  const overlayRef = useRef(null);
  const userSizeMultiplierRef = useRef(1);
  const currentZoomRef = useRef(null);
  const selectedPolylineRef = useRef(null);
  const hoveredPolylineRef = useRef(null);
  const selectedRoutesRef = useRef(new Set(selectedRoutes)); // Track selected routes for event handlers
  const airportLabelMarkersRef = useRef(new Map()); // airportId -> AdvancedMarkerElement
  const routeLabelMarkersRef = useRef(new Map()); // routeId -> AdvancedMarkerElement
  const airportLabelOffsetsRef = useRef(new Map()); // airportId -> { dx, dy }

  const hoverMultiplier = 1.2; // Marker icon mouseover size multiplier
  const selectedMultiplier = 1.05; // Slight bump for selected markers

  // useRef to to avoid async problems whenever isMarkerActive changes. It is used to stop mouseover event on polylines under a map marker if a map marker is being hovered over
  const isMarkerActiveRef = useRef(isMarkerActive);
  useEffect(() => {
    isMarkerActiveRef.current = isMarkerActive;
  }, [isMarkerActive]);

  const rangeCirclesRef = useRef(rangeCircles);
  useEffect(() => {
    rangeCirclesRef.current = rangeCircles;
  }, [rangeCircles]);

  const radiusRef = useRef(radius);
  useEffect(() => {
    radiusRef.current = radius;
  }, [radius]);

  // Keep track of current active polylines and prevent mouseover/out eventhandlers from changing the style of the polylines
  useEffect(() => {
    if (filters.selectionView) {
      selectedRoutesRef.current = new Set(selectionViewHighlightedRoutes);
    } else {
      selectedRoutesRef.current = new Set(selectedRoutes || []);
    }
  }, [selectedRoutes, selectionViewHighlightedRoutes, filters.selectionView]);

  const selectionViewRef = useRef(filters.selectionView);
  useEffect(() => {
    selectionViewRef.current = filters.selectionView;
  }, [filters.selectionView]);

  // When entering selectionView, clear per-view highlights
  useEffect(() => {
    if (filters.selectionView) {
      setSelectionViewHighlightedAirports([]);
      setSelectionViewHighlightedRoutes([]);
    }
  }, [filters.selectionView]);

  //update marker colors, when they are set by user
  useEffect(() => {
    setMarkerColors(
      Object.fromEntries(
        Object.entries(filters.colors).map(([key, value]) => [
          key,
          value[filters.colorIndex],
        ]),
      ),
    );
  }, [filters.colors, filters.colorIndex]);

  const loader = new Loader({
    apiKey: MAPS_KEY,
    version: "3.63", // 3.59 is the version before markers and advanced markers changed and broke our handling of them. This should be looked at at some point.
    libraries: ["places", "geometry", "marker"],
  });

  useEffect(() => {
    updateApiLoader(loader);
  }, [loader]);

  useEffect(() => {
    let storedCenter, storedZoom, center, zoom;

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const urlFilters = queryParams.get("filters");
      const filterData = JSON.parse(decompressObjectFromURL(urlFilters));

      storedCenter = filterData.mapCenter;
      storedZoom = filterData.mapZoom;

      // Additional validation to check if the parsed data has the expected structure and type
      if (
        typeof storedCenter?.lat === "number" &&
        typeof storedCenter?.lng === "number"
      ) {
        center = storedCenter;
      } else {
        throw new Error("Invalid center format");
      }

      if (typeof storedZoom.zoom === "number") {
        zoom = storedZoom.zoom;
      } else {
        throw new Error("Invalid zoom format");
      }
    } catch (error) {
      console.error(
        "Failed to load or parse map settings from local storage:",
        error,
      );
      // Fallback to default values in case of error
      center = { lat: 62.9595, lng: -1.07316 }; // Default center
      zoom = 5; // Default zoom
    }

    const onMapLoad = async () => {
      const initializedMap = await new window.google.maps.Map(
        googleMapRef.current,
        {
          center: center,
          zoom: zoom,
          minZoom: 3,
          maxZoom: 22,
          scrollwheel: true,
          zoomControl: false,
          fullscreenControl: false,
          disableDoubleClickZoom: true,
          mapTypeControl: false,
          cameraControl: false,
          streetViewControl: false,
          mapId: currentMapStyle.id || mapStyles.light.id,
          mapTypeId: filters.colorIndex === 3 ? "satellite" : "roadmap",
          clickableIcons: false,
        },
      );

      // Right-click listener for the map
      initializedMap.addListener("rightclick", (e) => {
        setIsConfirmationModalVisible(false);
        updateSearchBarIsOpen(false);
        setContextMenu({
          visible: true,
          x: e.domEvent.clientX,
          y: e.domEvent.clientY,
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        });
      });

      //debounced map center and zoom level updates
      const debouncedUpdateCenter = debounce((center) => {
        updateLastMapCenter(center);
      }, 2000); // milliseconds to wait after the last map center change before storing the new value

      // Adding event listeners with debounced handlers
      initializedMap.addListener("center_changed", () => {
        const center = {
          lat: Number(initializedMap.getCenter().lat().toFixed(4)),
          lng: Number(initializedMap.getCenter().lng().toFixed(4)),
        };
        debouncedUpdateCenter(center);
      });

      updateMap(initializedMap);

      // Initialize a reusable OverlayView
      const overlay = new google.maps.OverlayView();
      overlay.draw = function () {}; // Required but can be a no-op
      overlay.setMap(initializedMap);

      // Save the overlay in state or ref for later use
      overlayRef.current = overlay;
      mapRef.current = initializedMap;
    };

    if (!map) {
      loader.load().then(onMapLoad);
    }
  }, [map]);

  // Update the ref for zoom when it changes
  useEffect(() => {
    if (!map) return;

    // store last zoom to filters
    const debouncedUpdateZoom = debounce(() => {
      const zoom = map.getZoom();
      if (zoom != null) {
        updateLastMapZoom({ zoom: Math.round(zoom) });
      }
    }, 3000);

    // resize markers / polylines based on zoom
    const debouncedZoomScale = debounce(() => {
      const zoom = map.getZoom();
      const zoomMultiplier = calculateMultiplier(zoom);

      currentZoomRef.current = zoomMultiplier;

      applyCombinedScale();
      applyPolylineScale();
      repositionAirportLabels(); // keep selection view label offset in pixels
    }, 200);

    const listener = map.addListener("zoom_changed", () => {
      debouncedZoomScale();
      debouncedUpdateZoom();
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, updateLastMapZoom]);

  // Function to apply the combined scale (zoomMultiplier * userSizeMultiplier)
  function applyCombinedScale() {
    if (!map) return;

    markersRef.current.forEach((marker) => {
      const outer = marker._outer;
      const inner = marker._container; // the .markerScale element

      if (!outer || !inner) return;

      const isHovered = outer.isHovered || false;
      const isSelected = outer.isSelected || false;

      // Priority: hovered > selected > default
      let baseScale = 1;
      if (isHovered) {
        baseScale = hoverMultiplier;
      } else if (isSelected) {
        baseScale = selectedMultiplier;
      }

      const combinedScale = getMarkerScale(baseScale);
      inner.style.transform = `scale(${combinedScale})`;
    });
  }

  useEffect(() => {
    updateGeoJsonData(handleGeoJSON());
  }, []);

  useEffect(() => {
    const initializedMap = mapRef.current;
    if (!initializedMap || !geoJsonData) return;

    // Remove all existing features
    initializedMap.data.forEach((feature) =>
      initializedMap.data.remove(feature),
    );

    // Clear old listeners on the Data layer
    google.maps.event.clearListeners(initializedMap.data, "mouseover");
    google.maps.event.clearListeners(initializedMap.data, "mouseout");
    google.maps.event.clearListeners(initializedMap.data, "click");
    google.maps.event.clearListeners(initializedMap.data, "contextmenu");

    // Filter out countries that are already in filters.country
    const filteredGeoJsonData = {
      type: "FeatureCollection",
      features: geoJsonData.features.filter(
        (feature) => !filters.country.includes(feature.properties.iso),
      ),
    };

    // Load filtered GeoJSON data
    initializedMap.data.addGeoJson(filteredGeoJsonData);

    // Base style (invisible by default)
    initializedMap.data.setStyle({
      fillColor: "black",
      strokeColor: "#272727",
      strokeOpacity: 0,
      strokeWeight: 0,
      fillOpacity: 0,
    });

    // 🔒 In selectionView, stop here: no interactions, no hover
    if (filters.selectionView) {
      return () => {
        initializedMap.data.forEach((feature) =>
          initializedMap.data.remove(feature),
        );
        google.maps.event.clearListeners(initializedMap.data, "mouseover");
        google.maps.event.clearListeners(initializedMap.data, "mouseout");
        google.maps.event.clearListeners(initializedMap.data, "click");
        google.maps.event.clearListeners(initializedMap.data, "contextmenu");
      };
    }

    // --- Normal interactive behavior when NOT in selectionView ---

    // Style for idle
    initializedMap.data.setStyle({
      fillColor: "black",
      strokeColor: "#272727",
      strokeOpacity: 0,
      strokeWeight: 3,
      fillOpacity: 0,
    });

    const mouseoverListener = initializedMap.data.addListener(
      "mouseover",
      (event) => {
        const feature = event.feature;
        initializedMap.data.overrideStyle(feature, {
          strokeOpacity: 0.8,
          fillOpacity: 0.2,
        });
      },
    );

    const mouseoutListener = initializedMap.data.addListener(
      "mouseout",
      (event) => {
        const feature = event.feature;
        initializedMap.data.revertStyle(feature);
      },
    );

    const clickListener = initializedMap.data.addListener("click", (event) => {
      const feature = event.feature;
      const countryIso = feature.getProperty("iso");

      // Reset all necessary states
      clearSelectedAirports();
      clearSelectedRoutes();
      updateModalState(false);
      updateRouteModalState(false);
      setContextMenu({ visible: false });
      setIsConfirmationModalVisible(false);

      setGeoJsonMousePos({
        x: event.domEvent.clientX,
        y: event.domEvent.clientY,
      });

      const countryName = feature.getProperty("name");
      setSelectedCountry({ iso: countryIso, name: countryName });

      // Show confirmation modal
      setIsConfirmationModalVisible(true);
    });

    const contextMenuListener = initializedMap.data.addListener(
      "contextmenu",
      (event) => {
        event.stop(); // Prevent the polygon's right-click event

        const domEvent = event.domEvent || event;
        google.maps.event.trigger(initializedMap, "rightclick", {
          latLng: event.latLng,
          domEvent: {
            clientX: domEvent.clientX,
            clientY: domEvent.clientY,
          },
        });
      },
    );

    // Cleanup
    return () => {
      initializedMap.data.forEach((feature) =>
        initializedMap.data.remove(feature),
      );
      google.maps.event.removeListener(mouseoverListener);
      google.maps.event.removeListener(mouseoutListener);
      google.maps.event.removeListener(clickListener);
      google.maps.event.removeListener(contextMenuListener);
    };
  }, [filters.country, geoJsonData, map, filters.selectionView]);

  useEffect(() => {
    if (map) {
      if (
        filters &&
        filters.mapCenter &&
        filters.mapZoom &&
        filters.mapZoom.zoom
      ) {
        try {
          map.setCenter(filters.mapCenter);
          map.setZoom(filters.mapZoom.zoom);
        } catch (error) {
          console.log("Setting map center/zoom failed: ", error);
        }
      }
    }
  }, [restoreDefaultCount]);

  useEffect(() => {
    if (map) {
      const newMarkersRef = new Map();

      filteredAirportsResult?.forEach((airport) => {
        let marker = markersRef.current.get(airport.airportId);
        if (marker) {
          // Update the marker's position in case it has changed
          marker.position = new google.maps.LatLng(
            Number(airport.location.coordinates[1]),
            Number(airport.location.coordinates[0]),
          );

          // Update the color
          const newColor = filters.colors[airport.type][filters.colorIndex];
          const svgElement = marker.content.querySelector("circle");
          if (svgElement) {
            svgElement.setAttribute("fill", newColor);
          }
          marker.customColor = newColor;
        } else {
          // Create new marker if it doesn't exist
          marker = addMarker(
            airport,
            map,
            filters.colors[airport.type][filters.colorIndex],
          );
        }
        newMarkersRef.set(airport.airportId, marker);
      });

      // Remove old markers
      markersRef.current.forEach((marker, airportId) => {
        if (!newMarkersRef.has(airportId)) {
          marker.setMap(null);
        }
      });

      markersRef.current = newMarkersRef;
    } else {
      // Clear all markers when map is null
      markersRef.current.forEach((marker) => {
        marker.setMap(null);
      });
      markersRef.current = new Map();
    }
    setMarkersState(Date.now());
  }, [
    filteredAirportsResult,
    map,
    // selectedAirports,
    filters.colors,
    filters.colorIndex,
  ]);

  // Update the ref for userSizeMultiplier whenever it changes
  useEffect(() => {
    userSizeMultiplierRef.current = filters.userSizeMultiplier;

    // Apply the new userSizeMultiplier immediately
    applyCombinedScale();
  }, [filters.userSizeMultiplier]);

  useEffect(() => {
    if (map) {
      // Clear existing polylines
      polylinesRef.current.forEach((polyline) => polyline.setMap(null));
      polylinesRef.current = [];

      // Add new polylines
      filteredRoutesResult?.forEach((route) => {
        const polyline = addPolyline(route, map);
        polylinesRef.current.push(polyline);
      });
    } else {
      // Clear all polylines when map is null
      polylinesRef.current.forEach((polyline) => polyline.setMap(null));
      polylinesRef.current = [];
    }

    setPolylinesState(Date.now());
  }, [
    filteredRoutesResult,
    map,
    markerColors.domestic,
    markerColors.international,
    markerColors.customRoute,
    markerColors.commercialRoute,
    markerColors.psoRoute,
  ]);

  //close all tooltips while dragging/panning the map
  useEffect(() => {
    if (map) {
      const closeTooltips = () => setIsTooltipVisible(false);

      map.addListener("dragstart", closeTooltips);

      // remove the listener when the component unmounts or map changes
      return () => {
        google.maps.event.clearListeners(map, "dragstart");
      };
    }
  }, [map]);

  // Close UI elements when searchbar is opened
  useEffect(() => {
    if (searchBarIsOpen === true) {
      setIsConfirmationModalVisible(false);
      clearSelectedAirports();
      clearSelectedRoutes();
      updateModalState(false);
      updateRouteModalState(false);
      setContextMenu({ ...contextMenu, visible: false });
    }
  }, [searchBarIsOpen]);

  //track if map is being dragged/panned
  useEffect(() => {
    if (map) {
      // Listener to set dragging state to true and hide tooltips on drag start
      const onDragStart = () => {
        setIsMapDragging(true);
        setIsTooltipVisible(false);
      };

      // Listener to reset dragging state when dragging ends
      const onDragEnd = () => {
        setIsMapDragging(false);
        setIsTooltipVisible(false);

        if (activeMarker) {
          // Delay recalculating the tooltip's position to ensure it happens after the map has settled
          setTimeout(() => {
            updateTooltipPositionForMarker(
              activeMarker.airportId,
              activeMarker.airportLocation,
            );

            // Delay showing the tooltip to ensure it only appears after the position has been updated
            setTimeout(() => {
              setIsTooltipVisible(true);
            }, 50); // Adjust this delay as needed to smooth out the user experience
          }, 50); // This delay ensures map movement has settled
        }
      };

      // Listener for when the map becomes idle after movements
      const onMapIdle = () => {
        if (activeMarker) {
          // Here, updateTooltipPositionForMarker can be called without delay since we're in the idle state
          updateTooltipPositionForMarker(
            activeMarker.airportId,
            activeMarker.airportLocation,
            true,
          ); // Now ready to show tooltip
        }
      };

      map.addListener("idle", onMapIdle);
      map.addListener("dragstart", onDragStart);
      map.addListener("dragend", onDragEnd);

      // Cleanup function to remove event listeners
      return () => {
        google.maps.event.clearListeners(map, "dragstart");
        google.maps.event.clearListeners(map, "dragend");
        google.maps.event.clearListeners(map, "idle");
      };
    }
  }, [map, activeMarker, isTooltipVisible]);

  useEffect(() => {
    if (activeMarker) {
      setActiveMarkerData({
        ...activeMarker,
        airportData: filteredAirportsResult.find(
          (filteredAirport) =>
            filteredAirport.airportId === activeMarker.airportId,
        ),
      });
    }
  }, [activeMarker]);

  useEffect(() => {
    if (!map) return;

    const showLabels = filters.selectionView && filters.selectionAirportLabels;

    if (!showLabels) {
      airportLabelMarkersRef.current.forEach((m) => m.setMap(null));
      airportLabelMarkersRef.current.clear();
      return;
    }

    if (!overlayRef.current) return;

    const nextMap = new Map();

    (filteredAirportsResult || []).forEach((airport) => {
      const airportId = airport.airportId;
      if (!airportId || !airport.location?.coordinates) return;

      const lat = Number(airport.location.coordinates[1]);
      const lng = Number(airport.location.coordinates[0]);
      const baseLatLng = new google.maps.LatLng(lat, lng);

      let offset = airportLabelOffsetsRef.current.get(airportId);
      if (!offset) {
        offset = { dx: 0, dy: -32 }; // default offset from marker
        airportLabelOffsetsRef.current.set(airportId, offset);
      }

      const labelLatLng = offsetLatLng(baseLatLng, offset.dx, offset.dy);
      const labelText = airport.name || airport.airportName || airportId || "";

      let labelMarker = airportLabelMarkersRef.current.get(airportId);

      if (!labelMarker) {
        // CREATE NEW LABEL
        const container = document.createElement("div");
        container.className = style.airportLabel;

        const textSpan = document.createElement("span");
        textSpan.className = style.airportLabelText;
        textSpan.textContent = labelText;
        container.appendChild(textSpan);

        const arrowEl = document.createElement("div");
        arrowEl.className = style.airportLabelArrow;
        container.appendChild(arrowEl);

        // drag support
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let startDx = 0;
        let startDy = 0;

        const onMouseMove = (e) => {
          if (!isDragging) return;

          const deltaX = e.clientX - startX;
          const deltaY = e.clientY - startY;

          const newDx = startDx + deltaX;
          const newDy = startDy + deltaY;

          airportLabelOffsetsRef.current.set(airportId, {
            dx: newDx,
            dy: newDy,
          });

          const newLatLng = offsetLatLng(baseLatLng, newDx, newDy);
          labelMarker.position = newLatLng;
          updateAirportLabelArrow(labelMarker);
        };

        const onMouseUp = () => {
          if (!isDragging) return;
          isDragging = false;
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);
        };

        container.addEventListener("mousedown", (e) => {
          e.preventDefault();
          e.stopPropagation(); // don't pan map

          isDragging = true;
          startX = e.clientX;
          startY = e.clientY;

          const currentOffset = airportLabelOffsetsRef.current.get(
            airportId,
          ) || { dx: 12, dy: -16 };
          startDx = currentOffset.dx;
          startDy = currentOffset.dy;

          window.addEventListener("mousemove", onMouseMove);
          window.addEventListener("mouseup", onMouseUp);
        });

        labelMarker = new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position: labelLatLng,
          content: container,
          zIndex: 550,
        });

        labelMarker._baseLatLng = baseLatLng;
        labelMarker._arrowEl = arrowEl;
        labelMarker._airportId = airportId;
      } else {
        // UPDATE EXISTING LABEL – DO NOT overwrite children
        labelMarker._baseLatLng = baseLatLng;
        labelMarker.position = labelLatLng;

        if (labelMarker.content) {
          const textSpan = labelMarker.content.querySelector(
            `.${style.airportLabelText}`,
          );
          if (textSpan) textSpan.textContent = labelText;
        }

        labelMarker.map = map;
      }

      updateAirportLabelArrow(labelMarker);
      nextMap.set(airportId, labelMarker);
    });

    // remove labels for airports that are no longer visible
    airportLabelMarkersRef.current.forEach((marker, id) => {
      if (!nextMap.has(id)) {
        marker.setMap(null);
      }
    });

    airportLabelMarkersRef.current = nextMap;

    // final positioning safety
    repositionAirportLabels();
  }, [
    map,
    filters.selectionView,
    filters.selectionAirportLabels,
    filteredAirportsResult,
    markersState,
  ]);

  useEffect(() => {
    if (!map) return;

    const showDistance = !!filters.selectionRouteLabels;
    const showTime = !!filters.selectionRouteTime;
    const showLabels = filters.selectionView && (showDistance || showTime);

    if (!showLabels) {
      routeLabelMarkersRef.current.forEach((marker) => marker.setMap(null));
      routeLabelMarkersRef.current.clear();
      return;
    }

    const nextMap = new Map();

    (filteredRoutesResult || []).forEach((route) => {
      const id = route._id;
      if (!id || !route.lonlat_1 || !route.lonlat_2) return;

      const lat1 = Number(route.lonlat_1[1]);
      const lng1 = Number(route.lonlat_1[0]);
      const lat2 = Number(route.lonlat_2[1]);
      const lng2 = Number(route.lonlat_2[0]);

      // Simple midpoint (good enough for labels)
      const midLat = (lat1 + lat2) / 2;
      const midLng = (lng1 + lng2) / 2;

      const baseLatLng = new google.maps.LatLng(midLat, midLng);
      const labelLatLng = baseLatLng; // sits on the line

      const km =
        typeof route.distance === "number"
          ? `${Math.round(route.distance)} km`
          : "";

      const flightTime =
        filters.speed &&
        route.distance &&
        filters.speed > 0 &&
        route.distance > 0
          ? `${Math.ceil((route.distance / filters.speed) * 60)} min`
          : "";

      let labelMarker = routeLabelMarkersRef.current.get(id);

      if (!labelMarker) {
        // CREATE NEW LABEL
        const container = document.createElement("div");
        container.className = style.routeLabel;

        if (showDistance) {
          const distanceDiv = document.createElement("div");
          distanceDiv.className = style.routeLabelDistance;
          distanceDiv.textContent = km;
          container.appendChild(distanceDiv);
        }

        if (showTime) {
          const timeDiv = document.createElement("div");
          timeDiv.className = style.routeLabelTime;
          timeDiv.textContent = flightTime;
          container.appendChild(timeDiv);
        }

        labelMarker = new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position: labelLatLng,
          content: container,
          zIndex: 350,
        });
      } else {
        // UPDATE EXISTING LABEL
        labelMarker.position = labelLatLng;
        labelMarker.map = map;

        const container = labelMarker.content;
        if (container) {
          // Clear existing children and rebuild based on toggles
          container.innerHTML = "";

          if (showDistance) {
            const distanceDiv = document.createElement("div");
            distanceDiv.className = style.routeLabelDistance;
            distanceDiv.textContent = km;
            container.appendChild(distanceDiv);
          }

          if (showTime) {
            const timeDiv = document.createElement("div");
            timeDiv.className = style.routeLabelTime;
            timeDiv.textContent = flightTime;
            container.appendChild(timeDiv);
          }
        }
      }

      nextMap.set(id, labelMarker);
    });

    // Remove labels for routes that are no longer visible
    routeLabelMarkersRef.current.forEach((marker, id) => {
      if (!nextMap.has(id)) {
        marker.setMap(null);
      }
    });

    routeLabelMarkersRef.current = nextMap;
  }, [
    map,
    filters.selectionView,
    filters.selectionRouteLabels,
    filters.selectionRouteTime,
    filters.speed,
    filteredRoutesResult,
    polylinesState,
  ]);

  function offsetLatLng(latLng, dxPx, dyPx) {
    if (!overlayRef.current || !latLng) return latLng;
    const projection = overlayRef.current.getProjection();
    if (!projection) return latLng;

    const point = projection.fromLatLngToDivPixel(latLng);
    point.x += dxPx;
    point.y += dyPx; // positive is down

    return projection.fromDivPixelToLatLng(point);
  }

  function updateAirportLabelArrow(labelMarker) {
    if (!labelMarker?._arrowEl || !labelMarker.content) return;

    const doLayout = () => {
      // If the label is gone, abort.
      if (!labelMarker._arrowEl || !labelMarker.content) return;

      const labelRect = labelMarker.content.getBoundingClientRect();

      // If the label hasn't been laid out yet (width/height = 0),
      // try again on the next animation frame.
      if (!labelRect.width || !labelRect.height) {
        requestAnimationFrame(doLayout);
        return;
      }

      const labelCx = labelRect.left + labelRect.width / 2;
      const labelCy = labelRect.top + labelRect.height / 2;

      // Try to use the actual marker DOM center
      let targetX, targetY;
      const airportId = labelMarker._airportId;
      const marker =
        airportId && markersRef.current
          ? markersRef.current.get(airportId)
          : null;

      if (marker && marker.content) {
        const markerRect = marker.content.getBoundingClientRect();
        targetX = markerRect.left + markerRect.width / 2;
        targetY = markerRect.top + markerRect.height / 2;
      } else if (
        overlayRef.current &&
        labelMarker._baseLatLng &&
        mapRef.current
      ) {
        // Fallback: projection from LatLng
        const projection = overlayRef.current.getProjection();
        if (!projection) return;

        const basePoint = projection.fromLatLngToDivPixel(
          labelMarker._baseLatLng,
        );
        const mapRect = mapRef.current.getDiv().getBoundingClientRect();

        targetX = mapRect.left + basePoint.x;
        targetY = mapRect.top + basePoint.y;
      } else {
        return;
      }

      const dx = targetX - labelCx;
      const dy = targetY - labelCy;

      const angleRad = Math.atan2(dy, dx);
      const angleDeg = (angleRad * 180) / Math.PI;

      // Make the arrow sit on the edge of the label and “hug” it
      const halfWidth = labelRect.width / 2;
      const halfHeight = labelRect.height / 2;

      const cosA = Math.cos(angleRad) || 1e-3;
      const sinA = Math.sin(angleRad) || 1e-3;

      const distX = halfWidth / Math.abs(cosA);
      const distY = halfHeight / Math.abs(sinA);
      const distToEdge = Math.min(distX, distY);

      const overlap = 2; // px inside the label so there’s no visible gap
      const translateDist = distToEdge - overlap;

      labelMarker._arrowEl.style.transform = `translate(-50%, -50%) rotate(${angleDeg}deg) translateX(${translateDist}px)`;
    };

    // Kick off the layout once the browser has painted the marker content
    requestAnimationFrame(doLayout);
  }

  function repositionAirportLabels() {
    if (!overlayRef.current) return;

    airportLabelMarkersRef.current.forEach((labelMarker, airportId) => {
      const baseLatLng = labelMarker._baseLatLng;
      if (!baseLatLng) return;

      const offset = airportLabelOffsetsRef.current.get(airportId) || {
        dx: 12,
        dy: -16,
      };

      const newLatLng = offsetLatLng(baseLatLng, offset.dx, offset.dy);
      labelMarker.position = newLatLng;
      updateAirportLabelArrow(labelMarker);
    });
  }

  // function updateAirportLabelArrow(labelMarker) {
  //   if (
  //     !overlayRef.current ||
  //     !labelMarker?._baseLatLng ||
  //     !labelMarker._arrowEl
  //   ) {
  //     return;
  //   }

  //   const projection = overlayRef.current.getProjection();
  //   if (!projection) return;

  //   const baseLatLng = labelMarker._baseLatLng;
  //   const labelLatLng = labelMarker.position;
  //   if (!baseLatLng || !labelLatLng) return;

  //   const basePoint = projection.fromLatLngToDivPixel(baseLatLng);
  //   const labelPoint = projection.fromLatLngToDivPixel(labelLatLng);

  //   const dx = basePoint.x - labelPoint.x;
  //   const dy = basePoint.y - labelPoint.y;

  //   const angleRad = Math.atan2(dy, dx);
  //   const angleDeg = (angleRad * 180) / Math.PI;

  //   // arrow CSS has left:50%, top:50%, transform-origin at center-left
  //   labelMarker._arrowEl.style.transform = `translate(-50%, -50%) rotate(${angleDeg}deg)`;
  // }

  function getStrokeWeight(baseWeight) {
    const zoomMultiplier =
      currentZoomRef.current || calculateMultiplier(map.getZoom());
    return baseWeight * zoomMultiplier;
  }

  function getMarkerScale(baseScale = 1) {
    if (!map) return baseScale;
    const zoomMultiplier =
      currentZoomRef.current || calculateMultiplier(map.getZoom());
    const userMultiplier = userSizeMultiplierRef.current || 1;
    return baseScale * userMultiplier * zoomMultiplier;
  }

  // Function to update the position of the tooltip based on the marker's position.
  function updateTooltipPositionForMarker(airportId, airportLocation) {
    if (!map || !overlayRef.current || !airportId || !airportLocation) return;

    const projection = overlayRef.current.getProjection();
    if (!projection) return;

    const point = projection.fromLatLngToDivPixel(airportLocation);
    const mapContainerRect = map.getDiv().getBoundingClientRect();
    const pixelPoint = {
      x: point.x + mapContainerRect.right / 2,
      y: point.y * -1 + mapContainerRect.bottom / 2,
    };

    setActiveMarker({
      airportId: airportId,
      airportLocation: airportLocation,
      pixelPointKey: pixelPoint,
    });
  }

  const highlightMarker = (marker) => {
    const inner = marker?._container;
    const outer = marker?._outer;
    const svgElement = marker?._circle;

    if (svgElement && inner && outer) {
      outer.isSelected = true;
      inner.classList.add("selectedMarker");
      applyCombinedScale();
    }
  };

  const resetMarkerColor = (marker) => {
    const inner = marker?._container;
    const outer = marker?._outer;
    const svgElement = marker?._circle;

    if (svgElement && inner && outer) {
      outer.isSelected = false;
      inner.classList.remove("selectedMarker");
      applyCombinedScale();
    }
  };

  useEffect(() => {
    if (!markersRef.current) return;

    const idsToHighlight = filters.selectionView
      ? new Set(selectionViewHighlightedAirports) // per-view highlights
      : new Set(selectedAirports.map((a) => a.id)); // real selection

    markersRef.current.forEach((marker, airportId) => {
      if (idsToHighlight.has(airportId)) {
        highlightMarker(marker);
      } else {
        resetMarkerColor(marker);
      }
    });
  }, [
    selectedAirports,
    markersState,
    filters.selectionView,
    selectionViewHighlightedAirports,
  ]);

  const addMarker = (airport, map) => {
    const combinedScale = getMarkerScale(1);

    const advancedMarkerSvg = createAdvancedMarkerSvg(
      {
        airportType: airport.type,
        letter: airport.tour ? "T" : null,
      },
      markerColors,
    );

    const hoverColor = "#ffb700";

    const originalZIndex = 100; // Example baseline zIndex
    const hoverZIndex = 501; // Higher zIndex for hovered state

    // outer anchors, inner scales
    const outer = document.createElement("div");
    outer.classList.add("markerParent"); // keep your class
    outer.style.transform = "translate(-50%, -50%)"; // stable centering
    outer.style.position = "absolute";

    const inner = document.createElement("div");
    inner.classList.add("markerScale"); // applyCombinedScale targets this
    inner.style.transformOrigin = "center center";
    inner.style.transform = `scale(${combinedScale})`;

    inner.appendChild(advancedMarkerSvg);
    outer.appendChild(inner);

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map: map,
      position: {
        lat: Number(airport.location.coordinates[1]),
        lng: Number(airport.location.coordinates[0]),
      },
      content: outer, // outer is the content
    });

    marker.customColor = filters.colors[airport.type][filters.colorIndex]; // Attach the current color to the marker

    // store refs
    marker._container = inner; // scalable element
    marker._outer = outer; // anchored element
    marker._circle = advancedMarkerSvg.querySelector("circle");

    marker.addListener("click", (e) => {
      setContextMenu({ ...contextMenu, visible: false });
      setIsConfirmationModalVisible(false);
      updateSearchBarIsOpen(false);

      if (selectionViewRef.current) {
        // Per-view highlight toggle, does NOT touch selectedAirports
        setSelectionViewHighlightedAirports((prev) => {
          const exists = prev.includes(airport.airportId);
          return exists
            ? prev.filter((id) => id !== airport.airportId)
            : [...prev, airport.airportId];
        });
      } else {
        // Normal mode: toggle global selection (context)
        toggleSelectAirport({
          id: airport.airportId,
          coordinates: airport.location.coordinates,
        });
      }
    });

    // Add right-click handler for the range circle functionality
    marker.addListener("rightclick", (e) => {
      e.stop(); // Prevent the map's right-click event from firing
      setContextMenu({
        visible: true,
        x: e.domEvent.clientX,
        y: e.domEvent.clientY,
        lat: airport.location.coordinates[1], // MongoDB stores as [lng, lat]
        lng: airport.location.coordinates[0],
      });
    });

    // Store the original zIndex for easy reverting
    marker.originalZIndex = originalZIndex;
    marker.hoverZIndex = hoverZIndex;

    const markerCircle = marker._circle;

    const showTooltip = () => {
      setIsTooltipVisible(true);
    };

    // flags live on outer (the event target)
    outer.isHovered = false;
    outer.isSelected = false;

    // events on outer, scaling on inner
    outer.addEventListener("mouseover", () => {
      outer.style.zIndex = hoverZIndex;
      if (markerCircle) markerCircle.setAttribute("fill", hoverColor);

      updateTooltipPositionForMarker(airport.airportId, {
        lat: airport.location.coordinates[1],
        lng: airport.location.coordinates[0],
      });

      outer.isHovered = true;
      setIsMarkerActive(true);

      applyCombinedScale();

      if (!isMapDragging) {
        setTimeout(showTooltip, 10);
      }
    });

    outer.addEventListener("mouseout", () => {
      outer.style.zIndex = originalZIndex;
      if (markerCircle) markerCircle.setAttribute("fill", marker.customColor);

      setActiveMarker(null);
      setIsMarkerActive(false);
      setIsTooltipVisible(false);

      outer.isHovered = false;
      applyCombinedScale();
    });

    //right click event listener
    outer.addEventListener("contextmenu", (e) => {
      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        lat: marker.position.lat,
        lng: marker.position.lng,
      });
    });

    return marker;
  };

  // Base width for routes in "units" (pre-zoom)
  const POLYLINE_BASE_WIDTH = 3;
  const POLYLINE_SELECTED_WIDTH = 4; // slight bump when selected
  const POLYLINE_HOVER_WIDTH = 6; // bigger bump on hover

  function applyPolylineScale() {
    if (!map) return;

    polylinesRef.current.forEach((polyline) => {
      if (!polyline || !polyline.originalValues) return;

      const isHovered = !!polyline.isHovered;
      const isSelected = !!polyline.isSelected;

      let baseWidth = POLYLINE_BASE_WIDTH;

      if (isHovered) {
        baseWidth = POLYLINE_HOVER_WIDTH;
      } else if (isSelected) {
        baseWidth = POLYLINE_SELECTED_WIDTH;
      } else {
        // Fallback to whatever was defined when created
        baseWidth = polyline.originalValues.baseStrokeWeight;
      }

      polyline.setOptions({
        strokeWeight: getStrokeWeight(baseWidth),
      });
    });
  }

  const highlightPolyline = (polyline) => {
    if (map && polyline) {
      polyline.setOptions({
        strokeColor: "#FFD700",
        strokeOpacity: 0.9,
        zIndex: 400,
        // width controlled by applyPolylineScale()
      });
    }
  };

  const resetPolylineStyle = (polyline, originalValues) => {
    if (map && polyline) {
      polyline.setOptions({
        strokeColor: originalValues.color,
        strokeOpacity: originalValues.opacity,
        zIndex: originalValues.zIndex,
        // width controlled by applyPolylineScale()
      });
    }
  };

  // SELECTED ROUTE STYLE CHANGE
  // Highlight all selected routes
  useEffect(() => {
    const selectedSet = filters.selectionView
      ? new Set(selectionViewHighlightedRoutes)
      : new Set(selectedRoutes || []);

    polylinesRef.current.forEach((polyline) => {
      const isSelected = selectedSet.has(polyline.routeId);

      if (isSelected && !polyline.isSelected) {
        polyline.isSelected = true;
        highlightPolyline(polyline);
      } else if (!isSelected && polyline.isSelected) {
        polyline.isSelected = false;
        resetPolylineStyle(polyline, polyline.originalValues);
      }
    });

    applyPolylineScale();
  }, [
    selectedRoutes,
    polylinesState,
    filters.selectionView,
    selectionViewHighlightedRoutes,
  ]);

  const addPolyline = (route, map) => {
    const originalValues = {
      color: route.pso
        ? markerColors.psoRoute
        : route.commercial
          ? markerColors.commercialRoute
          : route.type === "custom"
            ? markerColors.customRoute
            : markerColors[route.type],
      zIndex: 200,
      opacity: 0.6,
      baseStrokeWeight: POLYLINE_BASE_WIDTH,
    };

    // const hoverValues = {
    //   color: "#FFD700",
    //   zIndex: 400,
    //   opacity: 0.9,
    //   strokeWeight: 10 * zoomMultiplier, // Increase stroke weight on hover, also dynamic based on zoom
    // };

    const polyline = new google.maps.Polyline({
      path: [
        {
          lat: Number(route.lonlat_1[1]),
          lng: Number(route.lonlat_1[0]),
        },
        {
          lat: Number(route.lonlat_2[1]),
          lng: Number(route.lonlat_2[0]),
        },
      ],
      strokeColor: originalValues.color,
      geodesic: true,
      strokeOpacity: originalValues.opacity,
      strokeWeight: getStrokeWeight(originalValues.baseStrokeWeight),
      map: map,
      zIndex: originalValues.zIndex,
    });

    polyline.originalValues = originalValues;
    polyline.routeId = route._id;
    polyline.isSelected = false;
    polyline.isHovered = false;

    const getMousePosition = (event) => {
      setMousePos({ x: event.clientX, y: event.clientY });
    };

    function handleMouseMove() {
      if (selectedRoutesRef.current.has(polyline.routeId)) return; // Do nothing if selected
      setTimeout(() => {
        if (!isMarkerActiveRef.current) {
          highlightPolyline(polyline);
        } else {
          resetPolylineStyle(polyline, originalValues);
        }
      }, 20);
    }

    //property to store the listener reference
    polyline.mouseMoveListener = null;

    polyline.addListener("mouseover", () => {
      polyline.isHovered = true;
      applyPolylineScale();

      // Existing code:
      window.addEventListener("mousemove", getMousePosition);

      if (!polyline.mouseMoveListener) {
        polyline.mouseMoveListener = polyline.addListener(
          "mousemove",
          handleMouseMove,
        );
      }

      setActivePolyline({ ...route });
      setIsPolylineActive(true);
    });

    polyline.addListener("mouseout", () => {
      // Always clear hover state & scale accordingly
      polyline.isHovered = false;
      applyPolylineScale();

      // If this route is selected, don't reset its color on mouseout
      if (selectedRoutesRef.current.has(polyline.routeId)) {
        if (polyline.mouseMoveListener) {
          google.maps.event.removeListener(polyline.mouseMoveListener);
          polyline.mouseMoveListener = null;
        }
        setIsPolylineActive(false);
        return;
      }

      window.removeEventListener("mousemove", getMousePosition);

      setTimeout(() => {
        resetPolylineStyle(polyline, originalValues);
      }, 25);

      if (polyline.mouseMoveListener) {
        google.maps.event.removeListener(polyline.mouseMoveListener);
        polyline.mouseMoveListener = null;
      }

      setActivePolyline(null);
      setIsPolylineActive(false);
      setCurrentPolyline(null);
    });

    polyline.addListener("rightclick", (e) => {
      setContextMenu({
        visible: true,
        x: e.domEvent.clientX,
        y: e.domEvent.clientY,
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      });
    });

    polyline.addListener("click", () => {
      setContextMenu({ ...contextMenu, visible: false });
      updateSearchBarIsOpen(false);

      if (selectionViewRef.current) {
        // Per-view highlight toggle
        setSelectionViewHighlightedRoutes((prev) => {
          const exists = prev.includes(route._id);
          return exists
            ? prev.filter((id) => id !== route._id)
            : [...prev, route._id];
        });
      } else {
        // Normal mode: toggle real selection
        toggleSelectRoute(route._id);
      }
    });
    return polyline;
  };

  useEffect(() => {
    if (!map) return;

    // Remove all previous click listeners (including the one from onMapLoad)
    google.maps.event.clearListeners(map, "click");

    const handleMapClick = () => {
      // Always close UI bits
      updateSearchBarIsOpen(false);
      setContextMenu((prev) => ({ ...prev, visible: false }));
      setIsConfirmationModalVisible(false);

      // Don't touch selection or modals when in selectionView
      if (!filters.selectionView) {
        clearSelectedAirports();
        clearSelectedRoutes();
        updateModalState(false);
        updateRouteModalState(false);
      }
    };

    const listener = map.addListener("click", handleMapClick);

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [
    map,
    filters.selectionView,
    clearSelectedAirports,
    clearSelectedRoutes,
    updateModalState,
    updateRouteModalState,
    updateSearchBarIsOpen,
  ]);

  function calculatePointNorthOfCenter(centerLat, centerLng, distanceKm) {
    const earthRadiusKm = 6371;
    const deltaDegrees = (distanceKm / earthRadiusKm) * (180 / Math.PI);
    const newLat = centerLat + deltaDegrees + 0.4; // Increased offset from center
    const newLng = centerLng; // Longitude remains the same when moving directly north or south
    return { lat: newLat, lng: newLng };
  }

  useEffect(() => {
    if (!radius || rangeCircles.length === 0) return;

    const newRadius = radius * 1000; // km -> m

    rangeCircles.forEach((rc) => {
      const currentCenter = rc.circle.getCenter();

      rc.circle.setRadius(newRadius);

      if (rc.distanceLabel?.content) {
        rc.distanceLabel.content.textContent = `${radius}\u00A0km`;
      }

      rc.distanceLabel.position = calculatePointNorthOfCenter(
        currentCenter.lat(),
        currentCenter.lng(),
        radiusRef.current,
      );

      rc.circle.setVisible(true);
      rc.circle.setOptions({ zIndex: 100 });
    });
  }, [radius, rangeCircles]);

  useEffect(() => {
    if (!mapFocusRequest) return;
    if (!map) return;

    centerMapOnPoint(
      map,
      mapFocusRequest.coordinates,
      mapFocusRequest.zoomLevel || 8,
    );
  }, [map, mapFocusRequest]);

  const addRangeCircle = () => {
    // Create range circle
    const newRangeCircle = new window.google.maps.Circle({
      strokeColor: "#095400",
      strokeOpacity: 0.8,
      strokeWeight: 1,
      fillColor: "#179800",
      fillOpacity: 0.35,
      map,
      center: { lat: contextMenu.lat, lng: contextMenu.lng },
      radius: radiusRef.current * 1000,
      zIndex: 400,
      draggable: true,
    });

    // Create range circle center marker
    const rangeCircleCenterContainer = document.createElement("div");
    rangeCircleCenterContainer.className = style.rangeCircleCenterContainer;

    const rangeCircleCenterDot = document.createElement("div");
    rangeCircleCenterDot.className = style.rangeCircleCenterDot;
    rangeCircleCenterContainer.appendChild(rangeCircleCenterDot);

    const rangeCircleCenterMarker =
      new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: contextMenu.lat, lng: contextMenu.lng },
        content: rangeCircleCenterContainer,
        zIndex: 101,
      });

    // Create range distance label
    const rangeDistanceContainer = document.createElement("div");
    rangeDistanceContainer.className = style.rangeDistanceContainer;

    const rangeDistanceText = document.createElement("div");
    rangeDistanceText.textContent = `${radiusRef.current}\u00A0km`;
    rangeDistanceContainer.appendChild(rangeDistanceText);

    const rangeDistancePosition = calculatePointNorthOfCenter(
      contextMenu.lat,
      contextMenu.lng,
      radiusRef.current,
    );

    const rangeDistanceLabel =
      new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: rangeDistancePosition,
        content: rangeDistanceContainer,
        zIndex: 101,
      });

    // Update positions when circle is dragged
    newRangeCircle.addListener("center_changed", () => {
      const center = newRangeCircle.getCenter();
      const updatedLabelPosition = calculatePointNorthOfCenter(
        center.lat(),
        center.lng(),
        radiusRef.current,
      );

      rangeCircleCenterMarker.position = center;
      rangeDistanceLabel.position = updatedLabelPosition;
    });

    newRangeCircle.addListener("rightclick", (e) => {
      setContextMenu({
        visible: true,
        x: e.domEvent.clientX,
        y: e.domEvent.clientY,
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      });
    });

    const newEntry = {
      circle: newRangeCircle,
      centerMarker: rangeCircleCenterMarker,
      distanceLabel: rangeDistanceLabel,
    };

    // append circle
    setRangeCircles((prev) => [...prev, newEntry]);

    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  const removeRangeCircle = () => {
    rangeCirclesRef.current.forEach((rc) => {
      rc.circle.setMap(null);
      rc.centerMarker?.setMap(null);
      rc.distanceLabel?.setMap(null);
    });

    setRangeCircles([]);
    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  const openCustomAirportForm = async () => {
    updateCustomAirportForm(false);
    updateEditingAirport(null);
    await delay(50);
    updateCustomAirportForm(true);
    setCustomAirportCoords({ lat: contextMenu.lat, lon: contextMenu.lng });
    setContextMenu({ ...contextMenu, visible: false });
  };

  const handleConfirm = () => {
    if (selectedCountry) {
      updateFilters((prevFilters) => {
        if (!prevFilters.country.includes(selectedCountry.iso)) {
          return {
            ...prevFilters,
            country: [...prevFilters.country, selectedCountry.iso],
          };
        }
        return prevFilters;
      });
    }
    setTimeout(() => {
      setIsConfirmationModalVisible(false);
    }, 250);
  };

  const handleCancel = () => {
    setTimeout(() => {
      setIsConfirmationModalVisible(false);
    }, 250);
  };

  const [currentMapStyle, setCurrentMapStyle] = useState(() => {
    // Use colorIndex from filters instead of localStorage
    const styleIndex = filters.colorIndex || 0;
    return Object.values(mapStyles)[styleIndex] || mapStyles.light;
  });

  const switchMapStyle = useCallback(
    (styleKey) => {
      const newStyle = mapStyles[styleKey];
      setCurrentMapStyle(newStyle);

      updateFilters((prev) => ({
        ...prev,
        colorIndex: newStyle.colorIndex,
      }));

      updateMap(null);
    },
    [updateMap, updateFilters],
  ); //default map style

  return (
    <>
      <div ref={googleMapRef} style={{ width: "100%", height: "100%" }} />
      {map && (
        <>
          <div className={style.searchAndUserButtonWrapper}>
            <SearchBar map={map} />
            <div className={style.menuButton}>
              <Fab
                color="primary"
                aria-label="open-menu"
                onClick={() => updateSummaryOpen(true)}
                sx={{ width: "2.5rem", height: "2.5rem" }}
              >
                <BarChartIcon className={style.menuIcon} />
              </Fab>
            </div>
            <CustomUserButton />
          </div>

          <BottomMapControls
            currentStyle={currentMapStyle}
            onStyleChange={switchMapStyle}
            updateFilters={updateFilters}
            map={map}
          />
        </>
      )}
      <ContextMenu
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
        onAddCircle={addRangeCircle}
        onRemoveCircle={removeRangeCircle}
        updateRadius={setRadius}
        radius={radius}
        openCustomAirportForm={openCustomAirportForm}
        rangeCircleCount={rangeCircles.length}
      />
      <LoadDataConfirmationModal
        visible={isConfirmationModalVisible}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        countryName={selectedCountry ? selectedCountry.name : ""}
        mousePosition={geoJsonMousePos}
      />
      {customAirportForm && (
        <CustomAirportForm coordinates={customAirportCoords} />
      )}
      {isTooltipVisible && !isMapDragging && isMarkerActive && (
        <MapMarkerTooltip markerData={activeMarkerData} />
      )}
      {mousePos &&
        !isMapDragging &&
        isPolylineActive &&
        airportIdMap &&
        !isMarkerActive &&
        activePolyline && (
          <PolylineTooltip
            polylineData={activePolyline}
            mousePosition={mousePos}
            airport1Data={airportIdMap.get(activePolyline.airport_1)}
            airport2Data={airportIdMap.get(activePolyline.airport_2)}
          />
        )}
    </>
  );
}
